import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Grow, IconButton } from "@mui/material";
import * as React from "react";

import {
  Notification,
  NotificationDispatchContext,
  NotificationSeverity,
  ShowNotification,
} from "./NotificationContext";
import styles from "./NotificationProvider.module.css";

// ``forwardRef`` + prop spread so the enclosing ``<Grow>`` can attach its
// ref and inject the transition ``style`` onto a real DOM node, exactly as
// it does for ``<Alert>``. Without this the grow/shrink animation would be a
// no-op for the accent variant.
const AccentNotification = React.forwardRef<
  HTMLDivElement,
  {
    message: string;
    onDismiss: () => void;
  } & React.HTMLAttributes<HTMLDivElement>
>(({ message, onDismiss, ...transitionProps }, ref) => {
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      className={styles.accent}
      {...transitionProps}
    >
      <InfoOutlinedIcon className={styles.accentIcon} fontSize="small" />
      <p className={styles.accentMessage}>{message}</p>
      <IconButton
        aria-label="Dismiss"
        onClick={onDismiss}
        size="small"
        className={styles.accentClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
});
AccentNotification.displayName = "AccentNotification";

function NotificationItem({
  notification,
  onDismiss,
}: {
  notification: Notification;
  onDismiss: (id: string) => void;
}) {
  const [exiting, setExiting] = React.useState(false);

  const handleDismiss = React.useCallback(() => {
    setExiting(true);
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(handleDismiss, notification.duration);
    return () => clearTimeout(timer);
  }, [notification.duration, handleDismiss]);

  return (
    <Grow
      in={!exiting}
      unmountOnExit
      onExited={() => onDismiss(notification.id)}
    >
      {/* Grow needs a single DOM child it can ref; both branches forward
          their props onto a plain element, so the transition applies
          whichever variant renders. */}
      {notification.variant === "accent" ? (
        <AccentNotification
          message={notification.message}
          onDismiss={handleDismiss}
        />
      ) : (
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={handleDismiss}
          className={styles.alert}
        >
          {notification.message}
        </Alert>
      )}
    </Grow>
  );
}

function NotificationDisplay({
  notifications,
  onDismiss,
}: {
  notifications: Notification[];
  onDismiss: (id: string) => void;
}) {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {notifications.map((n) => (
        <NotificationItem key={n.id} notification={n} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export function NotificationProvider({ children }: React.PropsWithChildren) {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);

  const showNotification = React.useCallback<ShowNotification>(
    (
      message,
      severity: NotificationSeverity = "success",
      duration = 3000,
      variant = "alert",
    ) => {
      setNotifications((prev) => [
        ...prev,
        { id: crypto.randomUUID(), message, severity, duration, variant },
      ]);
    },
    [],
  );

  const dismiss = React.useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationDispatchContext.Provider value={showNotification}>
      {children}
      <NotificationDisplay notifications={notifications} onDismiss={dismiss} />
    </NotificationDispatchContext.Provider>
  );
}
